import type { Observer } from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import HttpClient from '@naturehouse/nh-essentials/lib/requests/HttpClient';
import ApiRequestState from '../../../common/search/store/ApiRequestState';
import Timeout from '../../../enums/timeout';
import { modifyUrlParameters } from '../../../util/url';
import HouseSearchFilters from './HouseSearchFilters';
import HouseSearchListItems from './HouseSearchListItems';
import HouseSearchListItemsFactory from './HouseSearchListItemsFactory';

export default class HouseSearchApi implements Observer {
    static readonly #endpoint = `${window.location.origin}/api/search/houses`;

    readonly #listItems: HouseSearchListItems;

    readonly #apiRequestState: ApiRequestState;

    readonly #filters: HouseSearchFilters;

    #timeout: number | null = null;

    #abortController: AbortController | null = null;

    static #instance: HouseSearchApi | null = null;

    public static getInstance(): HouseSearchApi {
        if (HouseSearchApi.#instance === null) {
            HouseSearchApi.#instance = new HouseSearchApi(
                HouseSearchFilters.getInstance(),
                HouseSearchListItems.getInstance(),
                ApiRequestState.getInstance()
            );
        }

        return HouseSearchApi.#instance;
    }

    constructor(
        filters: HouseSearchFilters,
        listItems: HouseSearchListItems,
        apiRequestState: ApiRequestState
    ) {
        this.#filters = filters;
        this.#listItems = listItems;
        this.#apiRequestState = apiRequestState;

        this.#filters.forEach((filter) => {
            filter.attach(this);
        });
    }

    public update(): void {
        if (this.#timeout !== null) {
            window.clearTimeout(this.#timeout);
        }

        if (this.#abortController && this.#apiRequestState.state === ApiRequestState.REQUESTING) {
            this.#abortController.abort();
        }

        this.#apiRequestState.state = ApiRequestState.REQUESTING;
        this.#timeout = window.setTimeout(() => {
            this.#performSearch();
        }, Timeout.SEARCH_DELAY);
    }

    public static getSearchUrl(queryParameters: StandardObjectInterface): URL {
        return modifyUrlParameters(HouseSearchApi.#endpoint, queryParameters);
    }

    #performSearch(): void {
        this.#abortController = new AbortController();
        const params = this.#filters.activeParams;
        const searchParams = new URLSearchParams(params).toString();
        const endpoint = HouseSearchApi.#endpoint;

        const userHasPeriodSelectedKeys = ['arrivalDate', 'flexibleDurationType'];
        const userHasPeriodSelected = userHasPeriodSelectedKeys.some((key) => key in params);

        HttpClient.get(`${endpoint}?${searchParams}`, {
            signal: this.#abortController.signal
        })
            .then((response: unknown): void => {
                const houseSearchResponse = response as HouseSearchResponse;

                const houses = HouseSearchListItemsFactory.fromResponseData(houseSearchResponse);

                this.#listItems.setResults(
                    houses,
                    houseSearchResponse.total,
                    houseSearchResponse.groupedLocations,
                    userHasPeriodSelected,
                    houseSearchResponse.calculateTouristTaxHouseIds ?? []
                );
                this.#apiRequestState.state = ApiRequestState.REQUEST_SUCCESS;
            })
            .catch((): void => {
                this.#apiRequestState.state = ApiRequestState.REQUEST_FAILED;
            });
    }
}
